import type { PositionResponse } from "@/api/client";

/**
 * Pure cockpit math — the per-leg / per-cycle derivations the piloting panels share. Kept in one place so
 * the numbers are computed identically everywhere and can be unit-tested in isolation. Everything here is a
 * closed form of a position's (entry, stop, size, direction, openedAt) + the live mid / clock; nothing here
 * decides anything.
 */

export function dirSign(dir: string): 1 | -1 {
  return dir === "BUY" ? 1 : -1;
}

/**
 * Signed P&L if the leg's stop is hit right now: {@code (stop − entry)·dir·size}. NOT floored — a BUY stop
 * above entry / a SELL stop below entry (trailing in profit) returns its locked gain, so summed over the
 * book this is the "if every stop triggers now" outcome (which can be net positive).
 */
export function legStopoutEur(p: PositionResponse): number {
  return (Number(p.stopPrice) - Number(p.entryPrice)) * dirSign(p.direction) * Number(p.sizePerPointEur);
}

/**
 * §7 at-risk € of a leg — the LOSS if the stop is hit, floored at 0 (= {@code max(0, −stopout)}). BUY loses
 * {@code entry − stop}, SELL loses {@code stop − entry}; a profit-locked stop contributes 0, exactly
 * mirroring {@code PositionSnapshot.worstCaseLossEur} / the RiskManager leg.
 */
export function legRiskEur(p: PositionResponse): number {
  return Math.max(0, -legStopoutEur(p));
}

/** True when the leg's stop can no longer lose (contributes 0 to the worst-case) — i.e. profit-locked. */
export function isProfitLocked(p: PositionResponse): boolean {
  return legRiskEur(p) === 0;
}

/** Live latent (unrealized) € of a leg at {@code mid}: {@code (mid − entry)·dir·size}. */
export function legLatentEur(p: PositionResponse, mid: number): number {
  return (mid - Number(p.entryPrice)) * dirSign(p.direction) * Number(p.sizePerPointEur);
}

/**
 * UTC calendar nights a leg has been held (mirrors the mechanic's {@code finAccrue} day-count:
 * {@code floor(now/86400) − floor(open/86400)} in whole days). 0 for an intraday leg.
 */
export function nightsHeld(openedAtIso: string, nowMs: number): number {
  const open = Date.parse(openedAtIso);
  if (Number.isNaN(open)) return 0;
  const days = Math.floor(nowMs / 86_400_000) - Math.floor(open / 86_400_000);
  return Math.max(0, days);
}

/**
 * Overnight financing € accrued on a leg: {@code nights · entry · size · ratePct/100} (§3.2). Zero when
 * financing is disabled or the leg is intraday.
 */
export function financingEur(p: PositionResponse, nowMs: number, ratePct: number, enabled: boolean): number {
  if (!enabled) return 0;
  return nightsHeld(p.openedAt, nowMs) * Number(p.entryPrice) * Number(p.sizePerPointEur) * (ratePct / 100);
}

/** Current Paris hour (0..23) — for the LIVE session-window check. */
export function parisHour(nowMs: number): number {
  const h = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Paris",
    hour: "2-digit",
    hour12: false,
  }).format(nowMs);
  return Number(h) % 24;
}

/** Sum a per-leg € function over the open positions of one direction (or all when dir omitted). */
export function sumOpen(
  positions: PositionResponse[],
  fn: (p: PositionResponse) => number,
  dir?: "BUY" | "SELL",
): number {
  return positions
    .filter((p) => p.closedAt == null && (dir == null || p.direction === dir))
    .reduce((acc, p) => acc + fn(p), 0);
}
